import {Ray} from '@babylonjs/core/Culling/ray';
import {Matrix,Vector3} from '@babylonjs/core/Maths/math.vector';
import type {TargetCamera} from '@babylonjs/core/Cameras/targetCamera';
import {Action,TICK_RATE,groundHeightAt,launchProjectile,stepProjectile,type ArenaDefinition,type AttackDef,type PlayerState} from '@arena/shared';
import type {InputController} from './controller.ts';

interface Context {
  arena:ArenaDefinition;
  source:PlayerState;
  ready:boolean;
  /** Who flies off ("il Malebranche"). */
  label:string;
  /** The aimed move: its shot draws the path. */
  move:AttackDef;
  /** Where the floor arrow points now (stick, else facing): "S2 again" sends it there. */
  guide:{x:number;z:number};
}
type Direction={x:number;z:number};

/** Steps the chosen direction is held on the stick after the confirmation: the press's own buffer. */
const STEER_STEPS=10;

/**
 * Dante's devil where the player chooses (D44), in two steps like Virgilio:
 * S2 arms the aim and draws the devil's whole path on the floor (towards the
 * mouse pointer, else the floor arrow); a tap or a click on the arena sends it
 * that way, S2 again along the arrow, Esc cancels. The chosen direction goes
 * out as the stick for the step the move starts on: prediction and server
 * agree, nothing new on the wire.
 */
export class DirectionTargeting {
  private armed=false;
  /** The direction confirmed, held on the stick until the move starts. */
  private chosen:Direction|null=null;
  private steerSteps=0;
  /** Where the mouse points on the floor while aiming; null on touch. */
  private hover:Direction|null=null;
  private readonly button=document.querySelector<HTMLElement>('[data-button="Special2"]')!;
  private readonly hint=document.createElement('div');
  private readonly path=document.createElementNS('http://www.w3.org/2000/svg','svg');
  private readonly line=document.createElementNS('http://www.w3.org/2000/svg','path');
  private readonly head=document.createElementNS('http://www.w3.org/2000/svg','path');
  private readonly canvas:HTMLCanvasElement;
  private readonly camera:TargetCamera;
  private readonly controls:InputController;
  private readonly context:()=>Context|null;

  constructor(canvas:HTMLCanvasElement,camera:TargetCamera,controls:InputController,context:()=>Context|null){
    this.canvas=canvas;this.camera=camera;this.controls=controls;this.context=context;
    this.path.classList.add('targeting-range','devil-path');this.path.setAttribute('aria-hidden','true');
    this.line.setAttribute('fill','none');this.line.setAttribute('stroke-dasharray','12 7');
    this.head.setAttribute('fill','none');
    for(const p of [this.line,this.head]){p.setAttribute('stroke','#ff8a3a');p.setAttribute('stroke-width','3');p.setAttribute('stroke-linecap','round');}
    this.path.append(this.line,this.head);document.body.append(this.path);this.path.style.display='none';
    this.hint.className='targeting-hint devil-hint';this.hint.hidden=true;this.hint.setAttribute('role','status');
    document.body.append(this.hint);
    // Virgilio's aim answers the other buttons; this one takes S2.
    const previous=controls.onPress;
    controls.onPress=name=>{
      const handled=previous(name);
      if(name!=='Special2'){this.disarm();return handled;}
      const ctx=this.context();if(!ctx)return false;
      if(this.armed){this.confirm(ctx.guide);return true;}
      if(ctx.ready){
        this.armed=true;this.hover=null;
        this.hint.textContent=`Tocca dove mandare ${ctx.label} · S2 di nuovo: verso la freccia · Esc annulla`;
        this.update();
      }
      return true;
    };
    window.addEventListener('keydown',e=>{if(e.code==='Escape')this.disarm();});
    // The camera's drag zone lies over the canvas: the whole page is watched, the controls left out.
    document.addEventListener('pointermove',e=>{
      if(!this.armed || e.pointerType!=='mouse' || (e.target as HTMLElement).closest('button,select,input,#lobby,#home'))return;
      this.hover=this.pointAt(e);
    });
    document.addEventListener('pointerdown',e=>{
      if(!this.armed || e.button!==0)return;
      const el=e.target as HTMLElement;
      if(el.closest('button,select,input,#lobby,#home'))return;
      e.preventDefault();e.stopImmediatePropagation();
      const toward=this.pointAt(e);
      if(toward)this.confirm(toward);
    },{capture:true});
  }

  /**
   * Once per step sent: the direction to hold on the stick (world x, z), until
   * the aimed move has started or its press has run out.
   */
  steer(local:PlayerState|null):Direction|null{
    if(!this.chosen)return null;
    if(!local || local.action===Action.Special2 || --this.steerSteps<0){this.chosen=null;return null;}
    return this.chosen;
  }

  update():void{
    if(this.armed && !this.context()?.ready)this.disarm();
    this.button.classList.toggle('targeting',this.armed);
    this.button.setAttribute('aria-pressed',String(this.armed));
    this.hint.hidden=!this.armed;
    this.path.style.display=this.armed?'block':'none';
  }

  /** The devil's whole flight on the floor, up to the wall that would stop it. */
  updatePath():void{
    const ctx=this.armed?this.context():null;if(!ctx)return;
    const aim=this.hover??ctx.guide,s=ctx.source;
    const shot=launchProjectile({x:s.x,y:s.y,z:s.z,fx:aim.x,fz:aim.z},ctx.move);
    const points:Vector3[]=[];
    const floor=(x:number,z:number)=>groundHeightAt(ctx.arena,x,z,s.y+2)??s.y;
    points.push(new Vector3(s.x,floor(s.x,s.z)+.06,s.z));
    for(let i=0;i<ctx.move.projectile!.lifetime && stepProjectile(shot,ctx.arena,1/TICK_RATE);i++){
      if(i%3===2)points.push(new Vector3(shot.x,floor(shot.x,shot.z)+.06,shot.z));
    }
    points.push(new Vector3(shot.x,floor(shot.x,shot.z)+.06,shot.z));
    const rect=this.canvas.getBoundingClientRect(),viewport=this.camera.viewport.toGlobal(rect.width,rect.height);
    const transform=this.camera.getViewMatrix().multiply(this.camera.getProjectionMatrix());
    const screen=points.map(p=>Vector3.Project(p,Matrix.Identity(),transform,viewport)).filter(p=>p.z>0 && p.z<1);
    this.path.setAttribute('viewBox',`0 0 ${rect.width} ${rect.height}`);
    this.line.setAttribute('d',screen.map((p,i)=>`${i?'L':'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(''));
    // An arrowhead on the last stretch, along the screen direction of the flight.
    const [a,b]=[screen.at(-2),screen.at(-1)];
    if(!a || !b){this.head.setAttribute('d','');return;}
    const len=Math.hypot(b.x-a.x,b.y-a.y)||1,ux=(b.x-a.x)/len,uy=(b.y-a.y)/len,size=14;
    const wing=(side:number)=>`${(b.x-ux*size-uy*size*.6*side).toFixed(1)},${(b.y-uy*size+ux*size*.6*side).toFixed(1)}`;
    this.head.setAttribute('d',`M${wing(1)}L${b.x.toFixed(1)},${b.y.toFixed(1)}L${wing(-1)}`);
  }

  private confirm(toward:Direction):void{
    const len=Math.hypot(toward.x,toward.z);if(len<1e-3)return;
    this.chosen={x:toward.x/len,z:toward.z/len};this.steerSteps=STEER_STEPS;
    this.armed=false;this.controls.confirmTargetedAbility('Special2');this.update();
  }

  /** The direction from the caster to the floor under the pointer. */
  private pointAt(e:PointerEvent):Direction|null{
    const ctx=this.context();if(!ctx)return null;
    const rect=this.canvas.getBoundingClientRect();
    const ray=Ray.CreateNew(e.clientX-rect.left,e.clientY-rect.top,rect.width,rect.height,Matrix.Identity(),this.camera.getViewMatrix(),this.camera.getProjectionMatrix());
    if(Math.abs(ray.direction.y)<1e-6)return null;
    // The plane of the caster's feet is enough for a direction.
    const t=(ctx.source.y-ray.origin.y)/ray.direction.y;if(t<=0)return null;
    return {x:ray.origin.x+ray.direction.x*t-ctx.source.x,z:ray.origin.z+ray.direction.z*t-ctx.source.z};
  }

  /** Out of the aim; a direction already confirmed still steers its cast. */
  private disarm():void{
    if(!this.armed)return;
    this.armed=false;this.hover=null;
    this.update();
  }
}
